/**
 * Competitive - port from `1116/omega-club/dashboard/src/components/Competitive.jsx`.
 * Reads from `useTenantData(slug).competitiveChapters` + `.housingEconomics`.
 *
 * Per CHAP-86 (Phase 1C #52).
 *
 * Parity notes vs omega-club:
 * - omega-club uses 6 hardcoded semester columns (fa22..sp25). For
 *   chapter-build the semester set is driven from the union of keys
 *   across `semesterData` jsonb columns so tenants can carry their own
 *   semester labels.
 * - omega-club's Auburn-enrollment LineChart depends on a hardcoded
 *   `auburnEnrollment` array. The chapter-build TenantConfig doesn't
 *   surface university enrollment yet; that section is omitted until
 *   the schema extension lands (separate ticket).
 * - The closing "Lambda Chi's proposed 109 beds…" sentence is omega-zeta
 *   specific; reads from `tenant.displayName` for now but the insight
 *   text itself stays hardcoded for v1.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { formatNumber } from '../lib/formatters.js';
import type { CompetitiveChapter } from '../../shared/types.js';

export default function Competitive() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  // Derive semester column ordering by scanning all chapters' semesterData
  // keys. Sorts by inferred chronology (fa22 < sp23 < fa23 < sp24...).
  const semesters = useMemo<string[]>(() => {
    if (!data) return [];
    const set = new Set<string>();
    for (const ch of data.competitiveChapters) {
      if (ch.semesterData) {
        for (const k of Object.keys(ch.semesterData)) set.add(k);
      }
    }
    const arr = Array.from(set);
    arr.sort((a, b) => semesterSortKey(a).localeCompare(semesterSortKey(b)));
    return arr;
  }, [data]);

  const latestSem = semesters[semesters.length - 1];
  const earliestSem = semesters[0];

  // Top 10 by latest semester for the bar chart
  const top10 = useMemo(() => {
    if (!data || !latestSem) return [];
    return [...data.competitiveChapters]
      .map((row) => ({
        row,
        latestVal: Number(row.semesterData?.[latestSem] ?? 0),
      }))
      .sort((a, b) => b.latestVal - a.latestVal)
      .slice(0, 10)
      .map(({ row, latestVal }) => ({
        name: row.chapterName.length > 18 ? row.chapterName.slice(0, 18) + '…' : row.chapterName,
        fullName: row.chapterName,
        value: latestVal,
        highlighted: row.highlighted,
      }));
  }, [data, latestSem]);

  // Highlighted chapter rank
  const { highlightedChapter, highlightedRank } = useMemo(() => {
    if (!data || !latestSem) return { highlightedChapter: null, highlightedRank: null };
    const sorted = [...data.competitiveChapters].sort(
      (a, b) => Number(b.semesterData?.[latestSem] ?? 0) - Number(a.semesterData?.[latestSem] ?? 0)
    );
    const hl = sorted.find((c) => c.highlighted);
    return {
      highlightedChapter: hl ?? null,
      highlightedRank: hl ? sorted.findIndex((c) => c.id === hl.id) + 1 : null,
    };
  }, [data, latestSem]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;
  if (data.competitiveChapters.length === 0) {
    return <PageState label="No competitive chapters seeded for this tenant." />;
  }

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Header with hero callout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Competitive Analysis</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">
              {data.tenant.university ?? 'University'} fraternity membership trends
            </p>
          </div>
          {highlightedChapter && highlightedRank && latestSem && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">{data.tenant.displayName} rank</span>
              <span className="text-orange text-2xl sm:text-3xl font-bold">#{highlightedRank}</span>
              <span className="text-xs text-neutral">
                of {data.competitiveChapters.length} · {formatNumber(Number(highlightedChapter.semesterData?.[latestSem] ?? 0))} members {semesterLabel(latestSem)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Membership Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Fraternity Membership by Semester</h3>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-xs sm:text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2.5 px-2 sm:px-3 text-neutral font-medium sticky left-0 bg-white z-10">Fraternity</th>
                {semesters.map((sem) => (
                  <th key={sem} className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">
                    {semesterLabel(sem)}
                  </th>
                ))}
                {earliestSem && latestSem && earliestSem !== latestSem && (
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Trend</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.competitiveChapters.map((row) => (
                <ChapterRow
                  key={row.id}
                  row={row}
                  semesters={semesters}
                  earliestSem={earliestSem}
                  latestSem={latestSem}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top 10 Bar Chart */}
      {top10.length > 0 && latestSem && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Top 10 by {semesterLabel(latestSem)} Membership</h3>
          <div className="h-56 sm:h-64 lg:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top10} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#94a3b8' }} width={90} />
                <Tooltip
                  formatter={(value, _name, props) => {
                    const fullName = (props?.payload as { fullName?: string } | undefined)?.fullName;
                    return [value as number, fullName ?? ''];
                  }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                />
                <Bar dataKey="value" name={`${semesterLabel(latestSem)} Members`} radius={[0, 4, 4, 0]} fill="#3B82F6">
                  {top10.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.highlighted ? '#F26522' : '#3B82F6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Housing Economics */}
      {data.housingEconomics.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Housing Economics - Recent Rebuilds</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-xs sm:text-sm min-w-[480px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2.5 px-2 sm:px-3 text-neutral font-medium">Fraternity</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Year</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Beds</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Members</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Bed %</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Cost</th>
                  <th className="text-right py-2.5 px-2 sm:px-3 text-neutral font-medium">Rent/Mo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.housingEconomics.map((row) => {
                  const bedPct = row.beds && row.membership ? Math.round((row.beds / row.membership) * 100) : null;
                  return (
                    <tr key={row.id} className={row.highlighted ? 'bg-orange/10' : 'hover:bg-gray-50'}>
                      <td className={`py-2.5 px-2 sm:px-3 font-medium whitespace-nowrap ${row.highlighted ? 'text-orange-dark font-bold' : 'text-gray-700'}`}>
                        {row.chapterName}
                      </td>
                      <td className="py-2.5 px-2 sm:px-3 text-right">{row.year ?? 'n/a'}</td>
                      <td className={`py-2.5 px-2 sm:px-3 text-right ${row.highlighted ? 'font-semibold text-navy' : ''}`}>{row.beds ?? 'n/a'}</td>
                      <td className="py-2.5 px-2 sm:px-3 text-right">{row.membership ?? 'n/a'}</td>
                      <td className={`py-2.5 px-2 sm:px-3 text-right ${row.highlighted ? 'font-semibold text-navy' : ''}`}>
                        {bedPct == null ? 'n/a' : `${bedPct}%`}
                      </td>
                      <td className="py-2.5 px-2 sm:px-3 text-right">
                        {row.costEstimate == null ? 'n/a' : `$${(row.costEstimate / 1_000_000).toFixed(1)}M`}
                      </td>
                      <td className="py-2.5 px-2 sm:px-3 text-right">
                        {row.rentPerMonth == null ? 'n/a' : `$${row.rentPerMonth}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterRow({
  row,
  semesters,
  earliestSem,
  latestSem,
}: {
  row: CompetitiveChapter;
  semesters: string[];
  earliestSem?: string;
  latestSem?: string;
}) {
  const trend = earliestSem && latestSem && earliestSem !== latestSem
    ? Number(row.semesterData?.[latestSem] ?? 0) - Number(row.semesterData?.[earliestSem] ?? 0)
    : 0;
  const isHl = row.highlighted;
  return (
    <tr className={isHl ? 'bg-orange/10' : 'hover:bg-gray-50'}>
      <td className={`py-2.5 px-2 sm:px-3 font-medium sticky left-0 z-10 whitespace-nowrap ${isHl ? 'text-orange-dark bg-orange/10 font-bold' : 'text-gray-700 bg-white'}`}>
        {row.chapterName}
        {isHl && (
          <span className="ml-1.5 text-[10px] bg-orange text-white px-1.5 py-0.5 rounded-full">Ours</span>
        )}
      </td>
      {semesters.map((sem) => {
        const v = row.semesterData?.[sem];
        return (
          <td key={sem} className={`py-2.5 px-2 sm:px-3 text-right ${isHl ? 'font-semibold text-navy' : ''}`}>
            {v ?? 'n/a'}
          </td>
        );
      })}
      {earliestSem && latestSem && earliestSem !== latestSem && (
        <td className={`py-2.5 px-2 sm:px-3 text-right font-medium ${trend > 0 ? 'text-positive' : trend < 0 ? 'text-negative' : 'text-neutral'}`}>
          {trend > 0 ? '+' : ''}{trend}
        </td>
      )}
    </tr>
  );
}

/**
 * Lexicographic sort key for semester strings like fa22 / sp23.
 * fa22 → '2022-2-fa' (fall sorts after spring within same calendar year).
 */
function semesterSortKey(sem: string): string {
  const m = sem.toLowerCase().match(/^(fa|sp|su)(\d{2,4})$/);
  if (!m) return sem;
  const term = m[1]!;
  const yearStr = m[2]!;
  const year = yearStr.length === 2 ? `20${yearStr}` : yearStr;
  const order = term === 'sp' ? '1' : term === 'su' ? '2' : '3'; // SP < SU < FA within year
  return `${year}-${order}-${term}`;
}

function semesterLabel(sem: string): string {
  return sem.toUpperCase();
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
